import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FiBox, FiActivity, FiAlertTriangle, FiSearch, FiLoader } from 'react-icons/fi';

import SidebarNav from "../../components/MasterSideBar";
import TopbarNav from "../../components/TopbarNav";
import ProgramManagerInventoryTable from "../../components/programManager/ProgramManagerInventoryTable";
import MyProjectInventoryCharts from "../../components/programManager/MyProjectInventoryCharts";
import ReportUsageModal from "../../components/programManager/ReportUsageModal";
import ReturnItemsModal from "../../components/programManager/ReturnItemsModal";

const ProgramManagerInventoryPage = () => {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [inventoryData, setInventoryData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedProject, setSelectedProject] = useState("all");
    const [showUsageModal, setShowUsageModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const fetchInventory = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get("https://ims.holdthechild.net/api/inventory/my-projects-inventory", { headers: { Authorization: `Bearer ${token}` } });
            setInventoryData(response.data.data || []);
        } catch (err) { console.error("Syncing..."); } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchInventory(); }, [fetchInventory]);

    const filteredInventory = useMemo(() => inventoryData.filter(item => {
        const matchesSearch = item.itemName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesProject = selectedProject === "all" || item.issuedToProject?.projectTitle === selectedProject;
        return matchesSearch && matchesProject;
    }), [inventoryData, searchTerm, selectedProject]);

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" />
            <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} onLogout={() => { localStorage.clear(); navigate("/"); }} />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchTerm} />
                <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto">
                    <div className="max-w-[1600px] mx-auto space-y-6">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Project Inventory</h1>
                            <p className="text-gray-500 text-sm">Monitor stock levels across active projects.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                                <div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Stock Items</p><p className="text-3xl font-black text-gray-800">{filteredInventory.length}</p></div>
                                <div className="p-4 bg-orange-50 text-orange-600 rounded-xl"><FiBox size={24}/></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                                <div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Issued</p><p className="text-3xl font-black text-blue-600">{filteredInventory.reduce((s, i) => s + i.quantityIssued, 0)}</p></div>
                                <div className="p-4 bg-blue-50 text-blue-600 rounded-xl"><FiActivity size={24}/></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                                <div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Low Stock</p><p className="text-3xl font-black text-red-600">!</p></div>
                                <div className="p-4 bg-red-50 text-red-600 rounded-xl"><FiAlertTriangle size={24}/></div>
                            </div>
                        </div>

                        {!loading && <MyProjectInventoryCharts data={filteredInventory} />}

                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center">
                                <h2 className="text-sm font-black text-gray-800 uppercase tracking-widest">Inventory Ledger</h2>
                                <div className="relative w-64">
                                    <input type="text" placeholder="Filter..." onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-gray-50 border-none rounded-xl px-4 py-2 text-xs font-bold" />
                                    <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>
                            <div className="p-0">
                                {loading ? <div className="py-20 flex justify-center"><FiLoader className="animate-spin text-orange-500" /></div> : <ProgramManagerInventoryTable inventory={filteredInventory} onReportUsage={(i) => {setSelectedItem(i); setShowUsageModal(true);}} onReturnItems={(i) => {setSelectedItem(i); setShowReturnModal(true);}} />}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            {showUsageModal && <ReportUsageModal issuedItem={selectedItem} onClose={() => setShowUsageModal(false)} onSuccess={() => {fetchInventory(); setShowUsageModal(false);}} />}
            {showReturnModal && <ReturnItemsModal issuedItem={selectedItem} onClose={() => setShowReturnModal(false)} onSuccess={() => {fetchInventory(); setShowReturnModal(false);}} />}
        </div>
    );
};
export default ProgramManagerInventoryPage;